import { cn } from '@/lib/utils'
import { autoLoadMediaAtom } from '@/atoms/preferences'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import Image from './Image'
import ExternalLink from './ExternalLink'

type TWebMetadata = {
  title?: string | null
  description?: string | null
  image?: string | null
}

async function fetchWebMetadata(url: string): Promise<TWebMetadata> {
  const linkPreviewService = window.hallway.universe.linkPreviewService
  if (linkPreviewService) {
    try {
      const res = await fetch(`${linkPreviewService}/link/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      })
      const data = await res.json()
      return { title: data.title, description: data.description, image: data.image }
    } catch {
      return {}
    }
  }

  const genericCorsProxy = window.hallway.universe.genericCorsProxy
  if (genericCorsProxy) {
    url = genericCorsProxy.includes('{}')
      ? genericCorsProxy.replace('{}', encodeURIComponent(url))
      : `${genericCorsProxy}${encodeURIComponent(url)}`
  }

  try {
    const res = await fetch(url)
    const html = await res.text()
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const title =
      doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ||
      doc.querySelector('title')?.textContent
    const description =
      doc.querySelector('meta[property="og:description"]')?.getAttribute('content') ||
      (doc.querySelector('meta[name="description"]') as HTMLMetaElement | null)?.content
    const image = (doc.querySelector('meta[property="og:image"]') as HTMLMetaElement | null)
      ?.content

    return { title, description, image }
  } catch {
    return {}
  }
}

export default function WebPreview({
  url,
  className,
  variant = 'horizontal'
}: {
  url: string
  className?: string
  variant?: 'horizontal' | 'vertical'
  showFallback?: boolean
}) {
  const autoLoadMedia = useAtomValue(autoLoadMediaAtom)
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [metadata, setMetadata] = useState<TWebMetadata>({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    fetchWebMetadata(url).then((metadata) => {
      setMetadata(metadata)
      setIsLoading(false)
    })
  }, [url])

  const hostname = useMemo(() => {
    try {
      return new URL(url).hostname
    } catch {
      return ''
    }
  }, [url])

  if (isLoading) {
    return (
      <div className={cn('rounded-lg border overflow-hidden bg-card p-4 animate-pulse', className)}>
        <div className="h-4 bg-muted rounded w-3/4 mx-auto mb-2"></div>
        <div className="h-3 bg-muted rounded w-full mb-1"></div>
        <div className="h-3 bg-muted rounded w-5/6"></div>
      </div>
    )
  }

  if (!autoLoadMedia) {
    return <ExternalLink url={url} />
  }

  if (!metadata.title) {
    return <ExternalLink url={url} />
  }

  if (variant === 'vertical') {
    return (
      <div
        className={cn('rounded-lg border overflow-hidden bg-card', className)}
        onClick={(e) => {
          e.stopPropagation()
          window.open(url, '_blank')
        }}
      >
        <div className="p-3">
          <div className="text-xs text-muted-foreground mb-1">{hostname}</div>
          <div className="font-semibold line-clamp-2 mb-1">{metadata.title}</div>
          <div className="text-sm text-muted-foreground line-clamp-3">{metadata.description}</div>
        </div>
        {metadata.image && (
          <Image
            imageUrl={metadata.image}
            className="w-full h-48 rounded-none"
            hideIfError
            imgproxyModifiers="rs:fit:400:300"
          />
        )}
      </div>
    )
  }

  if (isSmallScreen && metadata.image) {
    return (
      <div
        className="rounded-lg border mt-2 overflow-hidden"
        onClick={(e) => {
          e.stopPropagation()
          window.open(url, '_blank')
        }}
      >
        <Image
          imageUrl={metadata.image}
          className="w-full h-44 rounded-none"
          hideIfError
          imgproxyModifiers="rs:fit:400:300"
        />
        <div className="bg-muted p-2 w-full">
          <div className="text-xs text-muted-foreground">{hostname}</div>
          <div className="font-semibold line-clamp-1">{metadata.title}</div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'p-0 cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30 flex w-full border rounded-lg overflow-hidden',
        className
      )}
      onClick={(e) => {
        e.stopPropagation()
        window.open(url, '_blank')
      }}
    >
      {metadata.image && (
        <Image
          imageUrl={metadata.image}
          className="aspect-[4/3] xl:aspect-video bg-foreground h-44 rounded-none"
          hideIfError
          imgproxyModifiers="rs:fit:400:300"
        />
      )}
      <div className="flex-1 w-0 p-2">
        <div className="text-xs text-muted-foreground">{hostname}</div>
        <div className="font-semibold line-clamp-2">{metadata.title}</div>
        <div className="text-xs text-muted-foreground line-clamp-5">{metadata.description}</div>
      </div>
    </div>
  )
}
